import { memo } from "react";

import { cn } from "@/lib/chillnn/cn";
import type { PriceRankItem } from "@/lib/gql/__generated__/graphql";

import { Button } from "@/components/ui/button/button";
import { InputColorField } from "@/components/ui/form/fields/input-field/input-color";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";
import Check from "@/components/ui/icon/check";
import Delete from "@/components/ui/icon/delete";
import EditDocument from "@/components/ui/icon/edit-document";

import { DialogEditPriceRank } from "./components";

import type { RoomPriceRankItemType } from "../../type";

type Props = {
	selectedPriceRank: PriceRankItem | undefined;
	onSetSelectedPriceRank: () => void;

	priceRankItem: RoomPriceRankItemType;

	onSetPriceRankName: (name: string) => void;
	onSetPriceRankColor: (color: string) => void;
	onSetIsStopSelling: (input: boolean | undefined) => void;

	removePriceRank: () => void;

	onSetPriceAmount: (roomID: string, amount: number | undefined, adultNum: number) => void;

	onSetRoomPriceRankItem: (value: RoomPriceRankItemType) => void;
};

export const RoomPriceRankListItem = memo(
	({
		selectedPriceRank, //
		onSetSelectedPriceRank,

		priceRankItem,

		onSetPriceRankColor,
		onSetPriceRankName,
		onSetIsStopSelling,

		removePriceRank,

		onSetPriceAmount,

		onSetRoomPriceRankItem,
	}: Props) => {
		// 選択されているかどうか
		const isSelected = selectedPriceRank?.priceRankID === priceRankItem.priceRankID.value;

		const isStopSelling = priceRankItem.isStopSelling?.value || false;

		return (
			<div className={cn(isSelected ? "border-2 border-brand" : "border-2 border-zinc-300", "relative rounded")}>
				{isSelected ? (
					<button
						type="button"
						className="absolute -top-5 left-4 rounded border-2 border-brand bg-white px-2 py-1"
					>
						<div className="flex gap-x-2">
							<Check className="text-brand" />
							<p className="bg-white">選択中</p>
						</div>
					</button>
				) : (
					<button
						onClick={onSetSelectedPriceRank}
						type="button"
						className="absolute -top-5 left-4 cursor-pointer rounded border-2 border-zinc-300 bg-white px-2 py-1 text-zinc-300 hover:text-zinc-950"
					>
						<div className="flex gap-x-2">
							<Check />
							<p className="bg-white">選択</p>
						</div>
					</button>
				)}

				<DialogEditPriceRank
					priceRankItem={priceRankItem}
					onSetIsStopSelling={onSetIsStopSelling}
					onSetPriceAmount={onSetPriceAmount}
					onSetRoomPriceRankItem={onSetRoomPriceRankItem}
					dialogTriggerChildren={({ onChangeOpen }) => {
						const handleClickEdit = () => {
							onChangeOpen(true);
						};
						const handleClickDelete = () => {
							if (window.confirm("この料金ランクを削除しますか？")) {
								removePriceRank();
							}
						};

						return (
							<div className="flex flex-col gap-y-4">
								{isStopSelling && <p className="text-sm font-semibold text-error-600">売止中</p>}
								<div className="flex gap-x-4">
									<div className="flex flex-1 items-center gap-x-2">
										<InputColorField
											handleChange={onSetPriceRankColor}
											value={priceRankItem.color.value}
											errorMessage={priceRankItem.color.error}
										/>

										<InputTextField
											className="flex-1"
											value={priceRankItem.name.value}
											handleChange={onSetPriceRankName}
											errorMessage={priceRankItem.name.error}
										/>
									</div>

									<div className="flex items-center gap-x-2">
										<Button
											variant="input-ish"
											size="icon"
											tooltip="部屋ごとに料金を設定できます"
											type="button"
											onClick={handleClickEdit}
										>
											<EditDocument size="sm" />
										</Button>
										<Button
											tooltip="削除"
											variant="input-ish"
											type="button"
											size="icon"
											onClick={handleClickDelete}
										>
											<Delete size="sm" />
										</Button>
									</div>
								</div>
							</div>
						);
					}}
				/>
			</div>
		);
	},
);

RoomPriceRankListItem.displayName = "RoomPriceRankListItem";
